import React, { useRef, useState, useCallback, useEffect, RefObject } from 'react';
import { DROPDOWN_ITEM } from 'consts';
import { Offset } from 'types/index';
import { DropdownItem } from './DropdownItem';
import { DropdownMenu } from './DropdownMenu';
import { DropdownSubmenu } from './DropdownSubmenu';
import { DropdownToggle } from './DropdownToggle';
import { Container } from './Dropdown.style';
import { InputError } from '../Inputs/InputError';
import { calculateDistance } from 'helpers/generic';
import { useMeasure, useOutsideClick } from 'hooks/utils';
import { useWindowSize } from 'hooks/ui';
import { DataTestId } from 'tests/consts';

interface Props {
	error?: string;
	menuStyles?: React.CSSProperties;
	className?: string;
	menuClassName?: string;
	children: React.ReactNode;
	button?: boolean;
	title?: string;
	titleComponent?: React.ReactNode;
	label?: string;
	disabled?: boolean;
	width?: number;
	offset?: Offset;
	initiallyOpen?: boolean;
	scrollable?: boolean;
	usePortal?: boolean;
	usedForCustomisableTable?: boolean;
	isPopup?: boolean;
	forceClose?: boolean;
	toggleId?: string;
	toggleComponent?: (props: {
		ref: React.RefObject<HTMLDivElement>;
		open: boolean;
		toggle: () => void;
	}) => React.ReactNode;
	shouldScrollIntoView?: 'end' | 'start' | 'center' | 'nearest' | boolean;
	closeOnResize?: boolean;
	placeholder?: string;
	_buttonRef?: RefObject<HTMLDivElement> | null;
	setForceClose?: (close: boolean) => void;
	onClose?: () => void;
	onOpen?: () => void;
	dataTestId?: string;
}

export function Dropdown({
	menuStyles,
	error,
	className,
	menuClassName,
	children,
	toggleComponent,
	button = false,
	usedForCustomisableTable = false,
	disabled,
	title,
	titleComponent,
	label,
	width,
	offset,
	initiallyOpen = false,
	scrollable = false,
	shouldScrollIntoView = true,
	usePortal,
	isPopup,
	placeholder,
	closeOnResize = false,
	forceClose = false,
	toggleId,
	_buttonRef,
	setForceClose,
	onClose,
	onOpen,
	dataTestId
}: Props) {
	const [open, setOpen] = useState(initiallyOpen);
	const [ref, bounds] = useMeasure<HTMLDivElement>();
	const menuRef = useRef<HTMLDivElement>(null);
	const [focused, setFocused] = useState(false);

	// CLOSE ON RESIZE
	const { width: windowWidth } = useWindowSize();
	useEffect(() => {
		if (!closeOnResize) return;
		onClose?.();
		setOpen(false);
	}, [closeOnResize, windowWidth]);

	useEffect(() => {
		if (forceClose) {
			toggle();
			setForceClose?.(false);
		}
	}, [forceClose]);

	const toggle = () => {
		if (disabled) return;

		const newOpen = !open;

		if (newOpen) {
			onOpen && onOpen();
		} else {
			onClose && onClose();
		}
		setOpen(newOpen);
	};

	const close = useCallback(() => {
		if (open) {
			onClose?.();
			setOpen(false);
		}
	}, [open, onClose]);

	useOutsideClick(close, [ref, menuRef]);

	// if it's scrollable, show first 5 elements and then add scroll
	const height = scrollable
		? 5 * (button ? DROPDOWN_ITEM.button.minHeight : DROPDOWN_ITEM.regular.minHeight)
		: undefined;
	const enhancedChildren = React.Children.toArray(children)
		.filter(child => child)
		.map(child => React.cloneElement(child as JSX.Element, { close, button }));

	const distance = calculateDistance(ref);

	return (
		<Container
			className={'custom-dropdown-container ' + className}
			id={className}
			data-testid={dataTestId}
		>
			{toggleComponent ? (
				toggleComponent({ ref, open, toggle })
			) : (
				<DropdownToggle
					focused={focused}
					setFocused={setFocused}
					placeholder={placeholder}
					ref={ref}
					_buttonRef={_buttonRef}
					open={open}
					title={title}
					titleComponent={titleComponent}
					label={label}
					disabled={disabled}
					error={error}
					toggle={toggle}
					dataTestId={DataTestId.DropdownToggle}
					id={toggleId ?? label?.replaceAll(' ', '').toLowerCase()}
				/>
			)}

			{open && (
				<DropdownMenu
					menuStyles={menuStyles}
					className={menuClassName}
					shouldScrollIntoView={shouldScrollIntoView}
					ref={menuRef}
					isPopup={isPopup}
					detached={usePortal}
					button={button}
					bounds={bounds}
					width={width}
					offset={offset}
					usedForCustomisableTable={usedForCustomisableTable}
					height={height}
					distance={distance}
				>
					{enhancedChildren}
				</DropdownMenu>
			)}

			<InputError error={error} />
		</Container>
	);
}

Dropdown.Item = DropdownItem;
Dropdown.Submenu = DropdownSubmenu;
